import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const HTMLCanvas = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Images & Graphics</div>
                <h1>Canvas Element Basics</h1>
                <p className="hero-lead">
                    Create dynamic, JavaScript-powered graphics with the HTML5 Canvas API. Learn to draw
                    shapes, text, images, and build interactive visualizations and games.
                </p>
            </div>

            <div className="content-section">
                <h2>1. The Programmatic Bitmap: Beyond Static Images</h2>
                <p>
                    The <code>&lt;canvas&gt;</code> element represents a monumental shift in web
                    graphics. While the <code>&lt;img&gt;</code> tag is a "read-only" window into
                    an external file, the canvas is a <strong>GPU-accelerated bitmap buffer</strong>
                    that you control frame-by-frame. It is the architectural foundation for
                    modern web-based games, real-time data visualizations, and high-fidelity
                    image processing.
                </p>
                <p>
                    Unlike its vector-based sibling, SVG, the canvas handles millions of pixels
                    with constant-time rendering performance. It doesn't care about the number
                    of "objects" you draw; it only cares about the pixels it needs to update.
                    This makes it the tool of choice for complex particle systems or high-frame-rate
                    simulations where SVG would struggle with DOM overhead.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>The Raster Paradigm</h3>
                        <p>Canvas operates in <strong>Immediate Mode</strong>. When you draw a shape,
                            the browser "forgets" it immediately after updating the pixels. You are
                            responsible for tracking state and managing the scene graph. This leads
                            to lower memory overhead for static scenes but higher complexity for interactive
                            ones where objects must be redrawn frequently.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Memory Footprint</h3>
                        <p>A canvas's memory cost is <code>(width * height * 4)</code> bytes. A
                            4K canvas consumes roughly 33MB of VRAM, regardless of whether it's
                            empty or contains 10,000 sprites. This predictability is a double-edged sword:
                            it prevents memory spikes but imposes a high "floor" for large canvases.</p>
                    </div>
                </div>

                <div className="content-section">
                    <h3>Internal Representation: The Graphics Buffer</h3>
                    <p>
                        When the browser encounters a <code>&lt;canvas&gt;</code> element, it allocates
                        a portion of system or GPU memory known as a <strong>Backing Store</strong>.
                        Every drawing operation you performbe it a simple stroke or a complex image
                        transformis translated into a sequence of low-level graphics commands that
                        modify this buffer.
                    </p>
                    <p>
                        The synchronization between your JavaScript code and the screen refresh is
                        handled by the <strong>Compositor Thread</strong>. This separation allows the
                        browser to keep the UI responsive (scrolling, clicking) even while you are
                        performing heavy pixel calculations on the main thread, provided you use
                        asynchronous patterns correctly.
                    </p>
                </div>

                <ProTip><p><strong>Professional Insight:</strong> The choice between Canvas and SVG
                        often boils down to "Object Quantity vs. Graphic Complexity." If you need
                        to manipulate individual shapes as DOM nodes, use SVG. If you need to paint
                        10,000 moving dots at 60 FPS, Canvas is your only viable path.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>2. Coordinate System Topology: Mastering the Grid</h2>
                <p>
                    By default, the <code>&lt;canvas&gt;</code> uses a grid where
                    <strong>(0,0) is the top-left corner</strong>. The X-axis increases to the
                    right, and the Y-axis increases <em>downwards</em>. This "inverted" Y-axis
                    is common in computer graphics but can be counter-intuitive for those
                    used to Cartesian coordinates in mathematics.
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Sub-Pixel Positioning</h3>
                        <p>Drawing at <code>(10.5, 10.5)</code> triggers <strong>Anti-Aliasing</strong>.
                            The browser interpolates colors across adjacent pixels, resulting in
                            "blurry" lines. For pixel-perfect strokes, always round your coordinates
                            or translate the context by <code>0.5</code>.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Context States</h3>
                        <p>The canvas maintains a <strong>State Stack</strong>. Using <code>ctx.save()</code>
                            and <code>ctx.restore()</code> allows you to push/pop transformations,
                            clipping paths, and styles, preventing global "style leakage" between components.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Basic Setup</h2>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- HTML: Create canvas element --&gt;
<canvas id="myCanvas" width="800" height="600">
    Your browser does not support HTML5 Canvas.
</canvas>

<script>
// JavaScript: Get canvas and context
const canvas = document.getElementById('myCanvas');
const ctx = canvas.getContext('2d');

// Now you can draw!
ctx.fillStyle = 'blue';
ctx.fillRect(10, 10, 100, 100);
</script>`} />
                </div>

                <div className="important-note">
                    <h3> Important Notes:</h3>
                    <ul>
                        <li>Set width/height in HTML attributes (not CSS)</li>
                        <li>CSS sizing stretches the canvas (pixelation)</li>
                        <li>Canvas coordinate system: (0,0) is top-left</li>
                        <li>Context types: '2d', 'webgl', 'webgl2'</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Drawing Rectangles</h2>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`const canvas = document.getElementById('myCanvas');
const ctx = canvas.getContext('2d');

// Filled rectangle
ctx.fillStyle = 'blue';
ctx.fillRect(10, 10, 150, 100);
// fillRect(x, y, width, height)

// Stroked rectangle (outline only)
ctx.strokeStyle = 'red';
ctx.lineWidth = 3;
ctx.strokeRect(180, 10, 150, 100);

// Clear rectangle (erase)
ctx.clearRect(50, 50, 50, 50);
// Creates transparent area`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Drawing Paths</h2>
                <p>Paths are sequences of points connected by lines or curves.</p>

                <h3>Basic Path</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`ctx.beginPath();        // Start new path
ctx.moveTo(50, 50);     // Move to starting point
ctx.lineTo(200, 50);    // Draw line to point
ctx.lineTo(200, 200);   // Another line
ctx.lineTo(50, 200);    // Another line
ctx.closePath();        // Close path (back to start)

// Fill or stroke the path
ctx.fillStyle = 'green';
ctx.fill();             // Fill the shape

ctx.strokeStyle = 'black';
ctx.lineWidth = 2;
ctx.stroke();           // Outline the shape`} />
                </div>

                <h3>Drawing a Circle</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`ctx.beginPath();
ctx.arc(100, 100, 50, 0, Math.PI * 2);
// arc(x, y, radius, startAngle, endAngle, counterclockwise)
// Angles in radians: Math.PI * 2 = 360
ctx.fillStyle = 'purple';
ctx.fill();`} />
                </div>

                <h3>Drawing an Arc</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`ctx.beginPath();
ctx.arc(150, 150, 100, 0, Math.PI); // Half circle (180)
ctx.strokeStyle = 'blue';
ctx.lineWidth = 5;
ctx.stroke();

// Convert degrees to radians:
const radians = degrees * (Math.PI / 180);`} />
                </div>

                <h3>Curves</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// Quadratic curve
ctx.beginPath();
ctx.moveTo(20, 100);
ctx.quadraticCurveTo(100, 20, 180, 100);
// quadraticCurveTo(controlX, controlY, endX, endY)
ctx.stroke();

// Bezier curve
ctx.beginPath();
ctx.moveTo(20, 150);
ctx.bezierCurveTo(50, 50, 150, 250, 200, 150);
// bezierCurveTo(cp1x, cp1y, cp2x, cp2y, endX, endY)
ctx.stroke();`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Drawing Text</h2>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// Filled text
ctx.font = '48px Arial';
ctx.fillStyle = 'black';
ctx.fillText('Hello Canvas!', 50, 100);

// Stroked text (outline)
ctx.strokeStyle = 'blue';
ctx.lineWidth = 2;
ctx.strokeText('Outlined Text', 50, 200);

// Text properties
ctx.font = 'bold 36px Helvetica';
ctx.textAlign = 'center';    // left, right, center, start, end
ctx.textBaseline = 'middle'; // top, middle, bottom, alphabetic
ctx.fillText('Centered', canvas.width / 2, canvas.height / 2);

// Measure text
const metrics = ctx.measureText('Hello');
console.log('Width:', metrics.width);`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Drawing Images</h2>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`const img = new Image();
img.onload = function() {
    // Draw at original size
    ctx.drawImage(img, 10, 10);
    
    // Draw with specific size
    ctx.drawImage(img, 10, 10, 200, 150);
    // drawImage(img, x, y, width, height)
    
    // Draw cropped image
    ctx.drawImage(img, 
        100, 100,      // Source x, y (crop from)
        200, 200,      // Source width, height (crop size)
        10, 10,        // Destination x, y (draw to)
        100, 100       // Destination width, height (draw size)
    );
};
img.src = 'image.jpg';`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Colors & Styles</h2>

                <h3>Fill & Stroke Styles</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// Solid colors
ctx.fillStyle = 'blue';
ctx.fillStyle = '#FF0000';
ctx.fillStyle = 'rgb(255, 0, 0)';
ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';

// Gradients
const gradient = ctx.createLinearGradient(0, 0, 200, 0);
gradient.addColorStop(0, 'red');
gradient.addColorStop(0.5, 'yellow');
gradient.addColorStop(1, 'blue');
ctx.fillStyle = gradient;
ctx.fillRect(10, 10, 200, 100);

// Radial gradient
const radGrad = ctx.createRadialGradient(100, 100, 10, 100, 100, 100);
radGrad.addColorStop(0, 'white');
radGrad.addColorStop(1, 'blue');
ctx.fillStyle = radGrad;
ctx.fillRect(0, 0, 200, 200);`} />
                </div>

                <h3>Line Styles</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`ctx.lineWidth = 10;
ctx.lineCap = 'round';     // butt, round, square
ctx.lineJoin = 'round';    // miter, round, bevel

// Dashed lines
ctx.setLineDash([10, 5]);  // 10px dash, 5px gap
ctx.lineDashOffset = 0;

ctx.beginPath();
ctx.moveTo(10, 10);
ctx.lineTo(200, 10);
ctx.stroke();`} />
                </div>

                <h3>Transparency</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// Global alpha (affects all drawing)
ctx.globalAlpha = 0.5;
ctx.fillRect(10, 10, 100, 100);
ctx.globalAlpha = 1.0; // Reset

// Or use rgba colors
ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
ctx.fillRect(50, 50, 100, 100);`} />
                </div>
            </div>

            <div className="content-section">
                <h2>7. The GPU Pipeline: Understanding Render Performance</h2>
                <p>
                    Modern browsers use <strong>hardware acceleration</strong> to render the
                    canvas surface. When you issue a drawing command, the browser translates
                    it into a series of operations sent to the GPU. Understanding this
                    pipeline is the difference between a jerky 15 FPS experience and a
                    silky-smooth 60 FPS application.
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Draw Call Batching</h3>
                        <p>Every stroke and fill is a "Draw Call." Switching
                            colors (<code>fillStyle</code>) or fonts causes the GPU to flush its
                            current buffer, which is expensive. Batch similar operations together
                            to minimize these context switches.</p>
                    </div>
                    <div className="concept-card">
                        <h3>VRAM Swapping</h3>
                        <p>Extremely large canvases (e.g., 8000x8000) may exceed the GPU's
                            texture memory. When this happens, the browser swaps data to the
                            CPU, resulting in massive performance drops. Keep your canvases
                            within reasonable viewport-related dimensions.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>8. Strategic Animation Loops: High-Precision Throttling</h2>
                <p>
                    Animation in Canvas is achieved by repeatedly clearing and redrawing
                    the surface. While <code>setInterval</code> was used in the past, it ignores
                    the display's refresh rate and the browser's current load.
                </p>
                <p>
                    The <strong>requestAnimationFrame (rAF)</strong> API is the gold standard.
                    It syncs with the monitor's V-Sync (usually 60Hz or 144Hz) and automatically
                    stops when the tab is hidden, saving battery and CPU.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>The High-Precision Animation Boilerplate</span>
                    </div>
                    <CodeBlock language="javascript" code={`let lastTime = 0;
function mainLoop(currentTime) {
    // Calculate Delta Time (dt) to ensure frame-rate independence
    const dt = (currentTime - lastTime) / 1000;
    lastTime = currentTime;

    update(dt);
    draw();

    requestAnimationFrame(mainLoop);
}

function update(dt) {
    // Move objects based on seconds, not frames
    // speed = 100px/s => position += 100 * dt
}

requestAnimationFrame(mainLoop);`} />
                </div>
                <ProTip><p><strong>Senior Engineering Tip:</strong> For physics-heavy applications,
                        use a <strong>Fixed Timestep</strong>. While rAF is great for rendering,
                        physics calculations should happen at a constant interval (e.g., 60 times
                        per second) to prevent "tunneling" (objects moving through walls) at
                        low frame rates.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>9. Transformations: The Matrix Under the Hood</h2>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// Save current state
ctx.save();

// Translate (move origin)
ctx.translate(100, 100);
ctx.fillRect(0, 0, 50, 50); // Actually at (100, 100)

// Rotate (around origin)
ctx.rotate(Math.PI / 4); // 45 degrees
ctx.fillRect(0, 0, 50, 50);

// Scale
ctx.scale(2, 2); // Double size
ctx.fillRect(0, 0, 50, 50);

// Restore previous state
ctx.restore();

// Combined transformation
ctx.save();
ctx.translate(200, 200);
ctx.rotate(Math.PI / 6);
ctx.scale(1.5, 1.5);
ctx.fillRect(-25, -25, 50, 50); // Centered at origin
ctx.restore();`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Compositing</h2>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// How new shapes combine with existing content
ctx.globalCompositeOperation = 'source-over'; // Default (new on top)

// Other modes:
// 'destination-over' - New behind existing
// 'source-in' - New only where overlapping
// 'source-out' - New only where not overlapping
// 'destination-atop' - Existing on top of new
// 'lighter' - Colors add together
// 'multiply' - Colors multiply (darker)
// 'screen' - Inverse multiply (lighter)

ctx.fillStyle = 'red';
ctx.fillRect(50, 50, 100, 100);

ctx.globalCompositeOperation = 'source-in';
ctx.fillStyle = 'blue';
ctx.arc(100, 100, 60, 0, Math.PI * 2);
ctx.fill();`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Animation</h2>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`const canvas = document.getElementById('myCanvas');
const ctx = canvas.getContext('2d');

let x = 0;
let y = canvas.height / 2;
let dx = 2;
let dy = 1.5;
const radius = 20;

function animate() {
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw ball
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = 'blue';
    ctx.fill();
    
    // Update position
    x += dx;
    y += dy;
    
    // Bounce off edges
    if (x + radius &gt; canvas.width || x - radius < 0) {
        dx = -dx;
    }
    if (y + radius &gt; canvas.height || y - radius < 0) {
        dy = -dy;
    }
    
    // Continue animation
    requestAnimationFrame(animate);
}

animate();`} />
                </div>
            </div>

            <div className="content-section">
                <h2>10. Pixel Manipulation: The RGBA Buffer</h2>
                <p>
                    One of the most powerful features of the canvas is the ability to
                    directly access and modify the underlying pixel data via the
                    <code>getImageData()</code> method. This returns an
                    <code>ImageData</code> object containing a <strong>Uint8ClampedArray</strong>.
                </p>
                <p>
                    This array represents pixels in a linear stream: <code>[R1, G1, B1, A1, R2, G2, B2, A2...]</code>.
                    Because it is a clamped array, values are automatically restricted
                    between 0 and 255, which is ideal for color calculations.
                </p>
                <ProTip><p><strong>Optimization Note:</strong> <code>getImageData</code> and
                        <code>putImageData</code> involve moving data between the GPU and
                        CPU. This is a relatively slow operation. For real-time filters,
                        consider using <strong>CSS Filters</strong> or <strong>WebGL Shaders</strong>
                        which keep the processing on the GPU.</p></ProTip>
                <div className="code-example">
                    <div className="code-header">
                        <span>The Grayscale Filter Algorithm</span>
                    </div>
                    <CodeBlock language="javascript" code={`const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
const data = imageData.data;

for (let i = 0; i < data.length; i += 4) {
    // Average the R, G, and B values
    const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
    data[i]     = avg; // Red
    data[i + 1] = avg; // Green
    data[i + 2] = avg; // Blue
}
ctx.putImageData(imageData, 0, 0);`} />
                </div>
            </div>

            <div className="content-section">
                <h2>11. High DPI (Retina) Support: The Resolution Gap</h2>
                <p>
                    A common mistake is assuming that one CSS pixel equals one physical
                    pixel. On modern "Retina" or High-DPI displays, the
                    <strong>Device Pixel Ratio (DPR)</strong> might be 2.0 or 3.0.
                    If you don't account for this, your canvas drawings will look
                    blurry and pixelated.
                </p>
                <p>
                    To fix this, you must set the canvas coordinate space (attributes)
                    to the physical resolution, while keeping the display size (CSS)
                    at the logical resolution. Then, use <code>ctx.scale()</code> to
                    ensure your drawing coordinates remain consistent.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>Retina-Ready Logic</span>
                    </div>
                    <CodeBlock language="javascript" code={`const dpr = window.devicePixelRatio || 1;
const rect = canvas.getBoundingClientRect();

canvas.width = rect.width * dpr;
canvas.height = rect.height * dpr;

ctx.scale(dpr, dpr);
canvas.style.width = \`\${rect.width}px\`;
canvas.style.height = \`\${rect.height}px\`;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>10. Performance Optimization: The High-Fidelity Toolkit</h2>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Layered Canvases</h3>
                        <p>Instead of redrawing everything on one canvas, use multiple
                            overlapping canvases. A static background canvas only draws
                            once, while a dynamic foreground canvas handles the moving
                            sprites. This drastically reduces the pixel-fill requirement per frame.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>11. Canvas Security: The 'Tainted' Surface</h2>
                <p>
                    Privacy is a major concern when manipulating pixels. If you draw an
                    image from a different origin (CORS) onto your canvas, the canvas
                    becomes <strong>"Tainted."</strong> Once tainted, you can no longer
                    use <code>getImageData()</code> or <code>toDataURL()</code>.
                </p>
                <ProTip><p><strong>Security Error:</strong> <em>"The canvas has been tainted by
                        cross-origin data."</em> This prevents malicious scripts from 'scraping'
                        pixels from a user's private images or cross-site tracking pixels.
                        To fix this, the server must provide <code>Access-Control-Allow-Origin</code>,
                        and the image element must have <code>img.crossOrigin = "anonymous"</code>.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>12. The Leap to 3D: WebGL vs. 2D Context</h2>
                <p>
                    While this lesson focuses on the <code>"2d"</code> context, the
                    <code>&lt;canvas&gt;</code> is also the host for <strong>WebGL</strong>
                    (Web Graphics Library). WebGL provides an API for 3D hardware-accelerated
                    graphics using GLSL (OpenGL Shading Language).
                </p>
                <p>
                    Web development is currently seeing the rise of <strong>WebGPU</strong>,
                    the next-generation standard that provides even lower-level access
                    to GPU hardware, allowing for AAA-quality games and massive
                    parallel computing directly in the browser via the canvas element.
                </p>
            </div>

            <div className="content-section">
                <h2>13. The State Stack: A Deep Archeology</h2>
                <p>
                    Every time you call <code>ctx.save()</code>, the browser takes a
                    complete snapshot of the current <strong>Drawing State</strong>. This
                    is not just a backup of coordinates; it is a stack-based capture
                    of the entire transformation matrix, clipping regions, and global
                    attributes like <code>shadowBlur</code>, <code>globalAlpha</code>,
                    and <code>lineDashOffset</code>.
                </p>
                <p>
                    Professional developers use the state stack to build
                    <strong>Recursive Scene Graphs</strong>. For example, when drawing a
                    humanoid character, you might save the state at the "torso," translate
                    to the "shoulder," draw the "arm," and then <code>restore()</code> to
                    return to the torso before drawing the other arm. This prevents the
                    transformations of the first arm from affecting the second.
                </p>
            </div>

            <div className="content-section">
                <h2>14. Browser Optimization Topology: Auditing Your Canvas</h2>
                <p>
                    A 'Super-Premium' canvas implementation isn't just about what
                    appears on the screenit's about how the browser's
                    <strong>Compositor Thread</strong> handles it. Opening the
                    Chrome DevTools 'Rendering' tab and enabling 'Paint Flashing'
                    will reveal if your canvas is triggering unnecessary full-page
                    repaints.
                </p>
                <p>
                    If your canvas is updating at 60 FPS, the browser should only be
                    repainting the canvas element itself (indicated by a green
                    rectangle). If the entire page is flashing, you likely have an
                    uncontained layout shift or a transparency issue that is forcing
                    the browser to re-composite the entire DOM tree against the
                    changing canvas pixels.
                </p>
                <ProTip><p><strong>Senior Engineering Insight:</strong> Use the
                        <code>will-change: transform</code> CSS property on your
                        canvas element. This hints to the browser that the element
                        will be updated frequently, allowing it to promote the
                        canvas to its own <strong>GPU Layer</strong>, which
                        prevents main-thread jank during heavy scrolling or
                        interactive animations.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>15. Mathematical Intuition: Understanding Winding</h2>
                <p>
                    The "nonzero" winding rule is often confusing. Imagine a
                    point inside a complex, self-overlapping path. To determine
                    if that point is "inside" (and thus filled), the computer
                    draws a ray from that point to infinity in any direction.
                    Every time the path crosses the ray, the computer adds 1
                    if the path is going clockwise and subtracts 1 if it is
                    going counter-clockwise.
                </p>
                <p>
                    If the final sum is not zero, the point is inside. If it
                    is zero, the point is outside. The "evenodd" rule is
                    simpler: it just counts the number of times the ray
                    crosses any part of the path. This mathematical precision
                    is what allows Canvas to render complex typography and
                    sophisticated icons with perfect accuracy.
                </p>
            </div>

            <div className="content-section">
                <h2>16. Complete Example: Interactive Drawing</h2>
                <div className="code-example">
                    <CodeBlock language="html" code={`<canvas id="drawCanvas" width="800" height="600" 
        style="border: 1px solid black; cursor: crosshair;">
</canvas>

<script>
const canvas = document.getElementById('drawCanvas');
const ctx = canvas.getContext('2d');

let isDrawing = false;
let lastX = 0;
let lastY = 0;

canvas.addEventListener('mousedown', (e) => {
    isDrawing = true;
    [lastX, lastY] = [e.offsetX, e.offsetY];
});

canvas.addEventListener('mousemove', (e) => {
    if (!isDrawing) return;
    
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(e.offsetX, e.offsetY);
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    
    [lastX, lastY] = [e.offsetX, e.offsetY];
});

canvas.addEventListener('mouseup', () => isDrawing = false);
canvas.addEventListener('mouseout', () => isDrawing = false);
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>14. OffscreenCanvas: The Multi-Threading Frontier</h2>
                <p>
                    One of the biggest bottlenecks in web performance is the single-threaded
                    nature of JavaScript. Heavy canvas operations can block the Main Thread,
                    causing the UI to freeze. <strong>OffscreenCanvas</strong> allows you to
                    transfer a canvas's rendering control to a <strong>Web Worker</strong>.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>Delegating Render to a Worker</span>
                    </div>
                    <CodeBlock language="javascript" code={`// Main Thread
const canvas = document.getElementById('myCanvas');
const offscreen = canvas.transferControlToOffscreen();
const worker = new Worker('render-worker.js');
worker.postMessage({ canvas: offscreen }, [offscreen]);

// render-worker.js
onmessage = (e) => {
    const canvas = e.data.canvas;
    const ctx = canvas.getContext('2d');
    
    function animate() {
        // Draw logic...
        requestAnimationFrame(animate);
    }
    animate();
};`} />
                </div>
            </div>

            <div className="content-section">
                <h2>15. Accessibility & The Semantic Gap</h2>
                <p>
                    Because <code>&lt;canvas&gt;</code> is a bitmap, its contents are
                    <strong>invisible to screen readers</strong> and search engines. A
                    primitive canvas is essentially a "black box." Creating an accessible
                    canvas requires a dual-layered approach.
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>The Fallback DOM</h3>
                        <p>Place standard HTML elements (buttons, links, text) <em>inside</em>
                            the canvas tag. While invisible to sighted users, they provide a
                            semantic "shadow tree" for assistive technologies to navigate your
                            visual interface.</p>
                    </div>
                    <div className="concept-card">
                        <h3>ARIA Hit Regions</h3>
                        <p>Use <code>ctx.addHitRegion()</code> (where supported) or manual
                            coordinate checking to update ARIA labels dynamically. When a
                            user focus moves to a coordinate "button" on the canvas, the
                            corresponding fallback element should receive focus.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>16. Advanced Pathfinding: Winding Rules</h2>
                <p>
                    When drawing complex shapes that overlap themselves, the canvas uses
                    <strong>Winding Rules</strong> to determine which areas are "inside"
                    or "outside" the shape.
                </p>
                <ul>
                    <li><strong>nonzero (Default):</strong> Evaluates the direction of
                        the path segments to decide the fill.</li>
                    <li><strong>evenodd:</strong> Simply toggles filling every time a
                        path boundary is crossed.</li>
                </ul>
                <div className="code-example">
                    <div className="code-header">
                        <span>Complex Fills with Winding Rules</span>
                    </div>
                    <CodeBlock language="javascript" code={`ctx.beginPath();
ctx.arc(50, 50, 30, 0, Math.PI * 2, true);  // Clockwise
ctx.arc(50, 50, 15, 0, Math.PI * 2, false); // Counter-clockwise
ctx.fill("evenodd"); // Creates a 'donut' hole effect`} />
                </div>
            </div>

            <div className="content-section">
                <h2>17. Modern Architecture: The Component-Entity-System (CES) Pattern</h2>
                <p>
                    For complex projects like character-heavy games or intricate data visuals,
                    monolithic scripts quickly become unmaintainable. Professional
                    implementations use a <strong>CES pattern</strong> which decouples
                    logic from rendering.
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>The Entity Manager</h3>
                        <p>Instead of classes, use simple IDs for 'Entities.' Components are
                            raw data structures (e.g., Position, Velocity, Sprite), and Systems
                            are the logic blocks that operate on entities possessing specific
                            combinations of components. This allows for massive scaling with
                            minimal performance overhead.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Determinism and Snapshots</h3>
                        <p>By decoupling the state, you can easily save "Snapshots" of your
                            entire application. This is essential for features like "Rewind" in
                            games or "Undo/Redo" in graphic editors, as you only need to
                            store the raw data and re-run the render loop. It also facilitates
                            network synchronization for multiplayer experiences.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>18. Memory Management: Tiers of Allocation</h2>
                <p>
                    Memory management in Canvas is often misunderstood. While JavaScript's
                    garbage collector handles the objects, the **VRAM buffers** are managed by
                    the browser's graphics engine.
                </p>
                <p>
                    Frequent re-allocation of large canvases (e.g., resizing on every window resize
                    event) is a "Performance Killer." It forces the GPU to dump existing texture
                    memory and negotiate for new blocks, leading to visible stuttering.
                    <strong>Object Pooling</strong> for sprites and <strong>Canvas Pooling</strong>
                    for temporary filters are mandatory for high-end web apps.
                </p>
                <ProTip><p><strong>Senior Strategy:</strong> If your app uses transient canvases
                        (for example, to generate procedural textures), maintain a small
                        "Canary Cache" of hidden canvases and reuse them by clearing their
                        context instead of deleting and recreating them. This keeps the memory
                        footprint stable and avoids costly GPU context switches.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>19. Best Practices</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Set canvas size in HTML attributes</li>
                            <li>Use requestAnimationFrame for animations</li>
                            <li>Support high-DPI displays</li>
                            <li>Cache complex drawings</li>
                            <li>Save/restore context state</li>
                            <li>Clear canvas before redrawing</li>
                            <li>Handle browser compatibility</li>
                            <li>Provide fallback content</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Size canvas with CSS only</li>
                            <li>Use setInterval for animations</li>
                            <li>Forget to call beginPath()</li>
                            <li>Create new objects in animation loop</li>
                            <li>Manipulate pixels on every frame</li>
                            <li>Draw entire canvas when partial updates work</li>
                            <li>Forget memory management</li>
                            <li>Assume canvas is accessible (add alternative)</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Canvas vs SVG: When to Use What</h2>
                <div className="when-to-use">
                    <h3>Use Canvas For:</h3>
                    <ul>
                        <li>Games and animations</li>
                        <li>Pixel manipulation (filters, effects)</li>
                        <li>Dynamic visualizations with many objects</li>
                        <li>Real-time graphics</li>
                        <li>Photo editing tools</li>
                        <li>Particle systems</li>
                    </ul>

                    <h3>Use SVG For:</h3>
                    <ul>
                        <li>Static or simple animations</li>
                        <li>Logos and icons</li>
                        <li>Scalable graphics</li>
                        <li>Interactive charts/diagrams</li>
                        <li>When you need DOM access</li>
                        <li>Accessibility is critical</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>20. Advanced Techniques: The Multi-Line Text Problem</h2>
                <p>
                    A major limitation of the <code>fillText()</code> API is its lack of
                    native support for <strong>line breaks</strong> or text wrapping. To
                    render a paragraph, you must manually measure each word and
                    calculate coordinate offsets.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>A Simple Wrap Function</span>
                    </div>
                    <CodeBlock language="javascript" code={`function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
    const words = text.split(' ');
    let line = '';

    for(let n = 0; n < words.length; n++) {
        let testLine = line + words[n] + ' ';
        let metrics = ctx.measureText(testLine);
        if (metrics.width &gt; maxWidth && n &gt; 0) {
            ctx.fillText(line, x, y);
            line = words[n] + ' ';
            y += lineHeight;
        } else {
            line = testLine;
        }
    }
    ctx.fillText(line, x, y);
}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>21. Case Study: High-Performance Particle Systems</h2>
                <p>
                    Particle systems (explosions, smoke, rain) are where the canvas
                    truly shines. By using a typed array for particle data and
                    a single <code>draw()</code> loop, you can simulate thousands
                    of independent entities.
                </p>
                <p>
                    <strong>The Secret to Speed:</strong> Minimize context property
                    changes inside the loop. Instead of changing <code>fillStyle</code>
                    for every particle, group particles by color and draw them in
                    batches, or use a <strong>Texture Atlas</strong> where all
                    particles are drawn from a single large image source.
                </p>
            </div>

            <div className="content-section">
                <h2>22. The Horizon: WebGPU & Low-Level Graphics</h2>
                <p>
                    As of 2024, the web is transitioning from WebGL to <strong>WebGPU</strong>.
                    While WebGL was a port of OpenGL ES, WebGPU is a modern API designed
                    to match the performance of Vulkan, Metal, and Direct3D 12.
                    The <code>&lt;canvas&gt;</code> element remains the primary interface
                    for this new standard.
                </p>
                <p>
                    WebGPU allows for **Compute Shaders**, which means you can use the
                    GPU for massive parallel data processing (like machine learning or
                    complex physics simulations) that would be impossible with the standard
                    2D Canvas API. This represents the ultimate evolution of the canvas
                    from a simple drawing surface into a high-performance computation host.
                </p>
                <ProTip><p><strong>Learning Path:</strong> If you've mastered 2D Canvas, your
                        next logical step is Three.js (for high-level 3D) or WebGPU
                        (for low-level engine development). The mental model of
                        coordinate spaces and transformation matrices you've learned here
                        is directly applicable to those advanced technologies.</p></ProTip>
            </div>

        </div>
    );
};

export default HTMLCanvas;



